// src/components/BonusCouponModal.jsx
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  Badge,
  Button,
  useDisclosure,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { supabase } from "../utils/supabaseClient";

/**
 * BonusCouponModal
 * Displays bonus coupons for the current business.
 * - Queries coupons table where is_bonus = true.
 * - Uses integer businessId for filtering.
 */
export default function BonusCouponModal({ businessId }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchCoupons() {
      if (!businessId) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("coupons")
          .select("id, title, discount, expiry, claims")
          .eq("business_id", businessId) // ✅ integer ID
          .eq("is_bonus", true);

        if (error) throw error;
        setCoupons(data || []);
      } catch (err) {
        console.error("Failed to fetch coupons:", err);
        setCoupons([]);
      } finally {
        setLoading(false);
      }
    }

    fetchCoupons();
  }, [businessId]);

  return (
    <>
      <Button onClick={onOpen} colorScheme="green" size="sm">
        🎁 View Bonus Coupons
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>🎉 Your Bonus Coupons</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading ? (
              <Flex justify="center" align="center" minH="100px">
                <Spinner />
              </Flex>
            ) : (
              <VStack spacing={4} align="start">
                {coupons.map((c) => (
                  <VStack key={c.id} align="start" spacing={1}>
                    <Text fontWeight="bold">{c.title || "Untitled Coupon"}</Text>
                    <Text>{c.discount ? `${c.discount}` : "No discount specified"}</Text>
                    <Text fontSize="sm" color="gray.500">
                      Expires:{" "}
                      {c.expiry
                        ? new Date(c.expiry).toLocaleDateString()
                        : "No expiry date"}
                    </Text>
                    <Badge colorScheme={c.claims > 0 ? "green" : "yellow"}>
                      {c.claims > 0 ? "Claimed" : "Available"}
                    </Badge>
                  </VStack>
                ))}
                {coupons.length === 0 && (
                  <Text fontSize="sm" color="gray.500">
                    No bonus coupons yet.
                  </Text>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
